import { useEffect, useRef, useState } from "react";
import {
  FaAngular,
  FaAws,
  FaBuilding,
  FaCalendarAlt,
  FaCode,
  FaCss3,
  FaDocker,
  FaEnvelope,
  FaExternalLinkAlt,
  FaGitAlt,
  FaGithub,
  FaGraduationCap,
  FaHtml5,
  FaJava,
  FaLinkedin,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaNode,
  FaPhone,
  FaReact,
  FaRocket,
  FaStar,
  FaStickyNote,
  FaTasks,
  FaTerminal,
  FaUsersCog,
  FaVial,
} from "react-icons/fa";
import {
  SiDotnet,
  SiExpress,
  SiIonic,
  SiJest,
  SiMongodb,
  SiMysql,
  SiPostgresql,
  SiSpring,
  SiTypescript,
  SiVitest,
} from "react-icons/si";
import "./App.css";
import { useLanguage } from "./LanguageContext";

// Scroll reveal hook
function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${className} ${visible ? "visible" : ""}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function LanguageSelector() {
  const { language, setLanguage } = useLanguage();
  return (
    <div className="lang-selector">
      <button className={`lang-btn ${language === "en" ? "active" : ""}`} onClick={() => setLanguage("en")}>
        EN
      </button>
      <button className={`lang-btn ${language === "es" ? "active" : ""}`} onClick={() => setLanguage("es")}>
        ES
      </button>
    </div>
  );
}

function Hero({ t }) {
  const scrollToContent = () => {
    document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="hero">
      <div className="hero-prompt">init portfolio --user=diego-guzman</div>
      <h1 className="hero-name">
        <span className="line">
          <span>Diego Guzmán</span>
        </span>
        <span className="line">
          <span>Medrano</span>
        </span>
      </h1>
      <p className="hero-role">{t.hero.title}</p>
      <p className="hero-desc">{t.hero.description}</p>
      <p className="hero-location">
        <FaMapMarkerAlt size={10} style={{ marginRight: 6 }} />
        {t.hero.location}
      </p>
      <div className="hero-scroll" onClick={scrollToContent}>
        SCROLL
      </div>
    </section>
  );
}

function About({ t }) {
  return (
    <section id="about" className="section">
      <Reveal>
        <div className="section-header">01 // {t.about.title}</div>
      </Reveal>
      <Reveal delay={100}>
        <p className="about-text">{t.about.description}</p>
      </Reveal>
    </section>
  );
}

function Experience({ t }) {
  const jobs = [{ key: "softwareEngineer" }, { key: "middleDev" }, { key: "fullStack" }];

  return (
    <section id="experience" className="section">
      <Reveal>
        <div className="section-header">02 // {t.experience.title}</div>
      </Reveal>
      <Reveal delay={100}>
        <div className="exp-container">
          <div className="exp-header">
            <div className="exp-dot red" />
            <div className="exp-dot yellow" />
            <div className="exp-dot green" />
            <span className="exp-title">experience.log</span>
          </div>
          <div className="exp-content">
            {jobs.map((job, idx) => {
              const jobData = t.experience.jobs[job.key];
              return (
                <div key={job.key} className={`exp-item ${job.key === "softwareEngineer" ? "current-role" : ""}`}>
                  <div className="exp-top-row">
                    <div className="exp-role">{jobData.title}</div>
                    <div className="exp-period">
                      {job.key === "softwareEngineer" ? "[CURRENT] " : ""}
                      {jobData.period}
                    </div>
                  </div>
                  <div className="exp-company">@{jobData.company}</div>
                  <p className="exp-desc">{jobData.description}</p>
                  {jobData.tasks && (
                    <ul className="exp-tasks">
                      {jobData.tasks.map((task, i) => (
                        <li key={i}>{task}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function Education({ t }) {
  const courses = [
    { key: "multiplatform", year: "2024-2025" },
    { key: "master", year: "2022" },
    { key: "bootcamp", year: "2021" },
  ];

  return (
    <section id="education" className="section">
      <Reveal>
        <div className="section-header">04 // {t.education.title}</div>
      </Reveal>
      <div className="edu-grid">
        {courses.map((course, idx) => (
          <Reveal key={course.key} delay={idx * 100}>
            <div className="edu-card">
              <div className="edu-year">{course.year}</div>
              <div className="edu-title">
                <FaGraduationCap style={{ marginRight: 8, color: "var(--accent)" }} />
                {t.education.courses[course.key]}
              </div>
              <div className="edu-school">{t.education.institutions[course.key]}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function TechStack({ t }) {
  const techData = [
    { name: "Angular", icon: <FaAngular />, color: "#f92672" },
    { name: "Ionic", icon: <SiIonic />, color: "#66d9ef" },
    { name: "React", icon: <FaReact />, color: "#66d9ef" },
    { name: "React Native", icon: <FaReact />, color: "#66d9ef" },
    { name: "TypeScript", icon: <SiTypescript />, color: "#66d9ef" },
    { name: "HTML5", icon: <FaHtml5 />, color: "#fd971f" },
    { name: "CSS3", icon: <FaCss3 />, color: "#66d9ef" },
    { name: "Java", icon: <FaJava />, color: "#f92672" },
    { name: "Spring Boot", icon: <SiSpring />, color: "#a6e22e" },
    { name: "Node.js", icon: <FaNode />, color: "#a6e22e" },
    { name: "Express", icon: <SiExpress />, color: "#75715e" },
    { name: "C#", icon: <FaCode />, color: "#ae81ff" },
    { name: ".NET", icon: <SiDotnet />, color: "#66d9ef" },
    { name: "MongoDB", icon: <SiMongodb />, color: "#a6e22e" },
    { name: "MySQL", icon: <SiMysql />, color: "#66d9ef" },
    { name: "PostgreSQL", icon: <SiPostgresql />, color: "#66d9ef" },
    { name: "Playwright", icon: <FaVial />, color: "#a6e22e" },
    { name: "Jest", icon: <SiJest />, color: "#f92672" },
    { name: "Jasmine", icon: <FaVial />, color: "#e6db74" },
    { name: "Karma", icon: <FaVial />, color: "#fd971f" },
    { name: "Vitest", icon: <SiVitest />, color: "#a6e22e" },
    { name: "AWS", icon: <FaAws />, color: "#fd971f" },
    { name: "Docker", icon: <FaDocker />, color: "#66d9ef" },
    { name: "CI/CD", icon: <FaCode />, color: "#e6db74" },
    { name: "Git", icon: <FaGitAlt />, color: "#f92672" },
  ];

  return (
    <section id="skills" className="section">
      <Reveal>
        <div className="section-header">05 // {t.skills.title}</div>
      </Reveal>
      <Reveal delay={100}>
        <div className="tech-container">
          {techData.map((tech) => (
            <span key={tech.name} className="tech-tag" style={{ borderColor: tech.color + "40", color: tech.color }}>
              <span className="tech-icon" style={{ color: tech.color }}>
                {tech.icon}
              </span>
              {tech.name}
            </span>
          ))}
        </div>
      </Reveal>
    </section>
  );
}

function Portfolio({ t }) {
  return (
    <section id="portfolio" className="section">
      <Reveal>
        <div className="section-header">06 // {t.portfolio.title}</div>
      </Reveal>
      <Reveal delay={100}>
        <div className="nido-card">
          <div className="nido-header">
            <div className="nido-header-left">
              <div className="nido-icon">
                <FaRocket />
              </div>
              <div className="nido-name">{t.portfolio.nidoApp.name}</div>
            </div>
            <span className="nido-badge">v1.0.0</span>
          </div>
          <div className="nido-body">
            <p className="nido-tagline">{t.portfolio.nidoApp.tagline}</p>
            <p className="nido-desc">{t.portfolio.nidoApp.description}</p>
            <div className="nido-arch">
              <FaTerminal size={10} style={{ marginRight: 8 }} />
              {t.portfolio.nidoApp.architecture}
            </div>
            <div className="nido-features">
              <div className="nido-feature">
                <FaMoneyBillWave /> {t.portfolio.nidoApp.features.expenses}
              </div>
              <div className="nido-feature">
                <FaTasks /> {t.portfolio.nidoApp.features.tasks}
              </div>
              <div className="nido-feature">
                <FaStickyNote /> {t.portfolio.nidoApp.features.notes}
              </div>
              <div className="nido-feature">
                <FaCalendarAlt /> {t.portfolio.nidoApp.features.events}
              </div>
              <div className="nido-feature">
                <FaUsersCog /> {t.portfolio.nidoApp.features.admin}
              </div>
            </div>
            <div className="nido-links">
              <a
                href="https://github.com/TheGuzman/NIDO_APP"
                target="_blank"
                rel="noopener noreferrer"
                className="nido-link primary"
              >
                <FaGithub /> {t.portfolio.viewRepo}
              </a>
              <a
                href="https://github.com/TheGuzman/NIDO_APP/releases/tag/v1.0.0"
                target="_blank"
                rel="noopener noreferrer"
                className="nido-link secondary"
              >
                <FaExternalLinkAlt /> {t.portfolio.viewRelease}
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

function SoftSkills({ t }) {
  const skills = Object.values(t.softSkills.items);
  return (
    <section id="softskills" className="section">
      <Reveal>
        <div className="section-header">07 // {t.softSkills.title}</div>
      </Reveal>
      <div className="skills-grid">
        {skills.map((skill, idx) => (
          <Reveal key={skill} delay={idx * 50}>
            <div className="skill-item">
              <FaStar style={{ marginRight: 8, color: "var(--accent)" }} />
              {skill}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Languages({ t }) {
  const langs = [
    { name: t.languages.spanish, level: t.languages.native },
    { name: t.languages.english, level: "C1+" },
    { name: t.languages.german, level: "B1+" },
  ];

  return (
    <section id="languages" className="section">
      <Reveal>
        <div className="section-header">08 // {t.languages.title}</div>
      </Reveal>
      <div className="lang-grid">
        {langs.map((lang, idx) => (
          <Reveal key={lang.name} delay={idx * 100}>
            <div className="lang-item">
              <div className="lang-name">{lang.name}</div>
              <div className="lang-level">{lang.level}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Clients({ t }) {
  const clients = [
    { name: "Hispaled S.L", icon: <FaBuilding />, href: "https://hispaled.com/" },
    { name: "Kiota", icon: <FaCode />, href: "https://www.kiota.es/" },
    { name: "SureVision", icon: <FaUsersCog />, href: "https://www.surevision.com/" },
  ];
  return (
    <section id="clients" className="section">
      <Reveal>
        <div className="section-header">
          03 // <FaUsersCog style={{ marginRight: 8, color: "var(--accent)" }} /> {t.clients.title}
        </div>
      </Reveal>
      <div className="clients-grid">
        {clients.map((client, idx) => (
          <Reveal key={client.name} delay={idx * 100}>
            <a href={client.href} target="_blank" rel="noopener noreferrer" className="client-item">
              <span className="client-icon">{client.icon}</span>
              {client.name}
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Contact({ t }) {
  return (
    <section id="contact" className="section">
      <Reveal>
        <div className="section-header">09 // {t.contact.title}</div>
      </Reveal>
      <Reveal delay={100}>
        <p style={{ color: "var(--text-muted)", marginBottom: 32, fontSize: "1.1rem" }}>{t.contact.subtitle}</p>
      </Reveal>
      <div className="contact-grid">
        <Reveal delay={150}>
          <a href="tel:+34620334581" className="contact-item">
            <div className="contact-icon">
              <FaPhone />
            </div>
            <span className="contact-text">+34 620 334 581</span>
          </a>
        </Reveal>
        <Reveal delay={200}>
          <a href="mailto:dguzmanmedrano@gmail.com" className="contact-item">
            <div className="contact-icon">
              <FaEnvelope />
            </div>
            <span className="contact-text">dguzmanmedrano@gmail.com</span>
          </a>
        </Reveal>
        <Reveal delay={250}>
          <a href="https://github.com/TheGuzman" target="_blank" rel="noopener noreferrer" className="contact-item">
            <div className="contact-icon">
              <FaGithub />
            </div>
            <span className="contact-text">GitHub</span>
          </a>
        </Reveal>
        <Reveal delay={300}>
          <a
            href="https://www.linkedin.com/in/diegoguzmanmedrano/"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-item"
          >
            <div className="contact-icon">
              <FaLinkedin />
            </div>
            <span className="contact-text">LinkedIn</span>
          </a>
        </Reveal>
      </div>
    </section>
  );
}

function Footer({ t }) {
  return (
    <footer className="footer">
      <div>// {new Date().getFullYear()} Diego Guzmán Medrano</div>
      <div style={{ marginTop: 4, opacity: 0.5 }}>{t.footer.rights}</div>
    </footer>
  );
}

function App() {
  const { t } = useLanguage();

  return (
    <div className="app">
      <div className="noise-overlay" />
      <div className="scanlines" />
      <div className="grid-bg" />
      <LanguageSelector />
      <Hero t={t} />
      <About t={t} />
      <Experience t={t} />
      <Clients t={t} />
      <Education t={t} />
      <TechStack t={t} />
      <Portfolio t={t} />
      <SoftSkills t={t} />
      <Languages t={t} />
      <Contact t={t} />
      <Footer t={t} />
    </div>
  );
}

export default App;
